export * from "./contracts.js";

import type { ApiErrorPayload } from "./contracts.js";

export type ApiErrorKind =
  | "validation"
  | "unauthenticated"
  | "forbidden"
  | "not-found"
  | "gone"
  | "conflict"
  | "server"
  | "network"
  | "aborted"
  | "unexpected";

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number | null;
  readonly code: string;
  readonly path: string | null;
  readonly timestamp: string | null;
  readonly correlationId: string | null;

  constructor(options: {
    message: string;
    kind: ApiErrorKind;
    status?: number | null;
    code?: string;
    path?: string | null;
    timestamp?: string | null;
    correlationId?: string | null;
    cause?: unknown;
  }) {
    super(options.message, { cause: options.cause });
    this.name = "ApiError";
    this.kind = options.kind;
    this.status = options.status ?? null;
    this.code = options.code ?? "UNEXPECTED_ERROR";
    this.path = options.path ?? null;
    this.timestamp = options.timestamp ?? null;
    this.correlationId = options.correlationId ?? null;
  }
}

export type QueryValue = string | number | boolean | null | undefined;

export interface ApiRequestOptions<TBody = unknown>
  extends Omit<RequestInit, "body" | "headers" | "method"> {
  readonly responseType?: "json" | "blob";
  readonly body?: TBody;
  readonly headers?: HeadersInit;
  readonly query?: Readonly<Record<string, QueryValue | readonly QueryValue[]>>;
}

export interface ApiResponse<T> {
  readonly data: T;
  readonly status: number;
  readonly headers: Headers;
  readonly correlationId: string | null;
}

export interface ApiClientOptions {
  readonly baseUrl: string;
  readonly getAccessToken?: () => string | null;
  readonly onUnauthorized?: (error: ApiError) => void;
  readonly fetch?: typeof globalThis.fetch;
  readonly createCorrelationId?: () => string;
}

export interface ApiClient {
  request<TResponse, TBody = unknown>(
    method: string,
    path: string,
    options?: ApiRequestOptions<TBody>,
  ): Promise<ApiResponse<TResponse>>;
  get<TResponse>(path: string, options?: ApiRequestOptions<never>): Promise<ApiResponse<TResponse>>;
  post<TResponse, TBody = unknown>(
    path: string,
    body?: TBody,
    options?: ApiRequestOptions<never>,
  ): Promise<ApiResponse<TResponse>>;
  put<TResponse, TBody = unknown>(
    path: string,
    body?: TBody,
    options?: ApiRequestOptions<never>,
  ): Promise<ApiResponse<TResponse>>;
  patch<TResponse, TBody = unknown>(
    path: string,
    body?: TBody,
    options?: ApiRequestOptions<never>,
  ): Promise<ApiResponse<TResponse>>;
  delete<TResponse>(path: string, options?: ApiRequestOptions<never>): Promise<ApiResponse<TResponse>>;
}

function fallbackUuid(): string {
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, "0"));
  return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10).join("")}`;
}

function createCorrelationId(): string {
  return globalThis.crypto.randomUUID?.() ?? fallbackUuid();
}

function ensureApiPath(path: string): string {
  if (!path.startsWith("/api/")) {
    throw new Error(`CarePass API paths must start with /api/: ${path}`);
  }
  if (path.includes("?" ) || path.includes("#")) {
    throw new Error("Use the query option instead of embedding query or fragment values in an API path.");
  }
  return path;
}

function appendQuery(
  path: string,
  query: ApiRequestOptions["query"],
): string {
  if (!query) return path;
  const search = new URLSearchParams();
  for (const [key, rawValue] of Object.entries(query)) {
    const values = Array.isArray(rawValue) ? rawValue : [rawValue];
    for (const value of values) {
      if (value !== null && value !== undefined) search.append(key, String(value));
    }
  }
  const serialized = search.toString();
  return serialized ? `${path}?${serialized}` : path;
}

function joinBaseUrl(baseUrl: string, path: string): string {
  const normalizedBase = baseUrl.trim().replace(/\/$/, "");
  if (!normalizedBase) return path;
  if (normalizedBase === "/") return path;
  if (normalizedBase === "/api" && path.startsWith("/api/")) return path;
  if (normalizedBase.endsWith("/api") && path.startsWith("/api/")) {
    return `${normalizedBase.slice(0, -4)}${path}`;
  }
  return `${normalizedBase}${path}`;
}

function isNativeBody(value: unknown): value is BodyInit {
  return typeof value === "string"
    || (typeof Blob !== "undefined" && value instanceof Blob)
    || (typeof FormData !== "undefined" && value instanceof FormData)
    || (typeof URLSearchParams !== "undefined" && value instanceof URLSearchParams)
    || value instanceof ArrayBuffer
    || ArrayBuffer.isView(value)
    || (typeof ReadableStream !== "undefined" && value instanceof ReadableStream);
}

function serializeBody(value: unknown, headers: Headers): BodyInit | undefined {
  if (value === undefined) return undefined;
  if (isNativeBody(value)) return value;
  if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  return JSON.stringify(value);
}

async function readResponseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined;
  const text = await response.text();
  if (!text) return undefined;
  const contentType = response.headers.get("Content-Type") ?? "";
  if (contentType.includes("json")) {
    try {
      return JSON.parse(text) as unknown;
    } catch {
      throw new ApiError({
        message: "The server returned an invalid JSON response.",
        kind: "unexpected",
        status: response.status,
        code: "INVALID_JSON_RESPONSE",
      });
    }
  }
  return text;
}

function errorKind(status: number): ApiErrorKind {
  if (status === 400) return "validation";
  if (status === 401) return "unauthenticated";
  if (status === 403) return "forbidden";
  if (status === 404) return "not-found";
  if (status === 410) return "gone";
  if (status === 409) return "conflict";
  if (status >= 500) return "server";
  return "unexpected";
}

function isApiErrorPayload(value: unknown): value is ApiErrorPayload {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<ApiErrorPayload>;
  return typeof candidate.code === "string"
    && typeof candidate.message === "string"
    && typeof candidate.path === "string"
    && typeof candidate.timestamp === "string";
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const fetchImplementation = options.fetch ?? globalThis.fetch;
  if (!fetchImplementation) throw new Error("A Fetch API implementation is required.");
  const correlationFactory = options.createCorrelationId ?? createCorrelationId;

  const request: ApiClient["request"] = async <TResponse, TBody = unknown>(
    method: string,
    path: string,
    requestOptions: ApiRequestOptions<TBody> = {},
  ) => {
    const safePath = ensureApiPath(path);
    const {
      body: requestBody,
      headers: requestHeaders,
      query,
      responseType,
      ...requestInit
    } = requestOptions;
    const url = joinBaseUrl(options.baseUrl, appendQuery(safePath, query));
    const headers = new Headers(requestHeaders);
    const correlationId = headers.get("X-Correlation-ID") ?? correlationFactory();
    if (!headers.has("Accept")) headers.set("Accept", responseType === "blob" ? "*/*" : "application/json");
    headers.set("X-Correlation-ID", correlationId);

    const accessToken = options.getAccessToken?.();
    if (accessToken && !headers.has("Authorization")) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }

    try {
      const response = await fetchImplementation(url, {
        ...requestInit,
        method: method.toUpperCase(),
        body: serializeBody(requestBody, headers),
        credentials: "omit",
        headers,
      });
      const body = response.ok && responseType === "blob" ? await response.blob() : await readResponseBody(response);
      const responseCorrelationId = response.headers.get("X-Correlation-ID") ?? correlationId;

      if (!response.ok) {
        const payload = isApiErrorPayload(body) ? body : null;
        const error = new ApiError({
          message: payload?.message ?? `CarePass API request failed with status ${response.status}.`,
          kind: errorKind(response.status),
          status: response.status,
          code: payload?.code ?? `HTTP_${response.status}`,
          path: payload?.path ?? safePath,
          timestamp: payload?.timestamp ?? null,
          correlationId: responseCorrelationId,
        });
        if (response.status === 401) {
          try {
            options.onUnauthorized?.(error);
          } catch {
            // Session cleanup must not hide the original API response.
          }
        }
        throw error;
      }

      return {
        data: body as TResponse,
        status: response.status,
        headers: response.headers,
        correlationId: responseCorrelationId,
      };
    } catch (error) {
      if (error instanceof ApiError) throw error;
      if (requestInit.signal?.aborted || (error instanceof DOMException && error.name === "AbortError")) {
        throw new ApiError({
          message: "CarePass API request was aborted.",
          kind: "aborted",
          code: "REQUEST_ABORTED",
          correlationId,
          cause: error,
        });
      }
      throw new ApiError({
        message: "CarePass API request could not reach the server.",
        kind: "network",
        code: "NETWORK_ERROR",
        correlationId,
        cause: error,
      });
    }
  };

  return {
    request,
    get: (path, requestOptions) => request("GET", path, requestOptions),
    post: (path, body, requestOptions) => request("POST", path, { ...requestOptions, body }),
    put: (path, body, requestOptions) => request("PUT", path, { ...requestOptions, body }),
    patch: (path, body, requestOptions) => request("PATCH", path, { ...requestOptions, body }),
    delete: (path, requestOptions) => request("DELETE", path, requestOptions),
  };
}
